// src/pages/ProjectFlowly.jsx
import { useEffect, useState } from 'react';
import '../styles/ProjectPage.css';
import GalleryImage from '../components/GalleryImage';
import Lightbox from '../components/Lightbox';
import CaseStudyToc from '../components/CaseStudyToc';
import BackToTop from '../components/BackToTop';
import { projectsData } from '../data/projects';
import { InstagramIcon, AppleIcon } from '../components/BrandIcons';

const TOC_SECTIONS = [
    { id: 'projet', label: 'Le projet' },
    {
        id: 'travail',
        label: "Comment j'ai travaillé",
        children: [{ id: 'process', label: 'Process' }]
    }
];

const GALLERY = [
    { src: '/images/flowly/mascot-normal.png', alt: 'Flowly mascotte normale' },
    { src: '/images/flowly/mascot-wave.png', alt: 'Flowly qui salue' },
    { src: '/images/flowly/mascot-writing.png', alt: 'Flowly qui écrit dans un carnet' },
    { src: '/images/flowly/mascot-teacher.png', alt: 'Flowly en institutrice devant un tableau' },
    { src: '/images/flowly/mascot-sparkles.png', alt: 'Flowly qui célèbre' },
    { src: '/images/flowly/mascot-throne.png', alt: 'Flowly couronnée sur un trône' }
];

function SunIcon() {
    return (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="4" />
            <line x1="12" y1="2" x2="12" y2="4" />
            <line x1="12" y1="20" x2="12" y2="22" />
            <line x1="4" y1="12" x2="2" y2="12" />
            <line x1="22" y1="12" x2="20" y2="12" />
            <line x1="4.9" y1="4.9" x2="6.3" y2="6.3" />
            <line x1="17.7" y1="17.7" x2="19.1" y2="19.1" />
            <line x1="4.9" y1="19.1" x2="6.3" y2="17.7" />
            <line x1="17.7" y1="6.3" x2="19.1" y2="4.9" />
        </svg>
    );
}

function MoonIcon() {
    return (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M20.354 15.354A9 9 0 1 1 8.646 3.646a7 7 0 0 0 11.708 11.708z" />
        </svg>
    );
}

export default function ProjectFlowly() {
    const project = projectsData.find((p) => p.id === 'flowly');
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const [darkMode, setDarkMode] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="project-page">
            <main className="project-main figma-project-main">
                <div className={`figma-embed-wrapper mascot-hero ${darkMode ? 'is-dark' : ''}`}>
                    <div className="mascot-hero-grid">
                        {GALLERY.map((img, i) => (
                            <GalleryImage
                                key={img.src}
                                src={img.src}
                                alt={img.alt}
                                onClick={() => setLightboxIndex(i)}
                            />
                        ))}
                    </div>

                    <button
                        type="button"
                        className="mascot-theme-toggle"
                        onClick={() => setDarkMode((d) => !d)}
                        aria-label={darkMode ? 'Passer au thème clair' : 'Passer au thème sombre'}
                    >
                        {darkMode ? <SunIcon /> : <MoonIcon />}
                    </button>
                </div>

                <div className="project-content">
                    <h1>{project.title}</h1>
                    <p className="project-desc">{project.desc}</p>

                    <div className="project-actions">
                        <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="cta-btn">
                            Voir le site ↗
                        </a>
                        <a href={project.instagramUrl} target="_blank" rel="noopener noreferrer" className="back-btn">
                            <InstagramIcon /> Instagram ↗
                        </a>
                        <a href={project.appStoreUrl} target="_blank" rel="noopener noreferrer" className="back-btn">
                            <AppleIcon /> App Store ↗
                        </a>
                    </div>
                </div>
            </main>

            <CaseStudyToc sections={TOC_SECTIONS} />

            <section className="case-study case-study--inline">
                <section className="case-study-section">
                    <h2 id="projet">Le projet</h2>
                    <p>
                        Flowly est une application de suivi d'habitudes, portée par une mascotte du même
                        nom. J'ai personnellement participé à la création de cette mascotte, aujourd'hui
                        visible sur le site, dans l'app et sur les réseaux du produit.
                    </p>
                </section>

                <section className="case-study-section">
                    <h2 id="travail">Comment j'ai travaillé</h2>
                    <p>
                        Le travail a commencé par plusieurs échanges pour comprendre précisément le
                        besoin : le ton, la personnalité, les usages dans lesquels la mascotte allait
                        apparaître. Plusieurs allers-retours ont suivi pour ajuster la proposition à la
                        demande du client, notamment sur la palette de couleurs, jusqu'aux rendus finaux
                        présentés ci-dessus.
                    </p>

                    <h3 id="process">Process</h3>
                    <video
                        className="process-video"
                        src="/images/flowly/process-timelapse.mov"
                        poster="/images/preview/preview-flowly.jpg"
                        autoPlay
                        loop
                        muted
                        playsInline
                    />
                </section>

                <p className="case-study-note">
                    Illustrations présentées à titre de portfolio uniquement. Merci de ne pas les
                    réutiliser, les copier ou les republier sans mon autorisation.
                </p>
            </section>

            <Lightbox
                images={GALLERY}
                index={lightboxIndex}
                onClose={() => setLightboxIndex(null)}
                onNavigate={setLightboxIndex}
            />

            <BackToTop />
        </div>
    );
}
